<template>
  <div class="card shadow table-card mb-4" v-if="loading || steps.length">
    <div class="form-card-header">
      <div class="section-number"><i class="fe fe-git-commit" style="font-size: 12px;"></i></div>
      <div class="flex-grow-1">
        <h6 class="mb-0 font-weight-bold text-white">Referral chain</h6>
        <small style="color: rgba(255,255,255,.75);">Where the patient came from and was referred on, with who decided each step</small>
      </div>
      <span v-if="steps.length" class="badge badge-light" style="color: #1e3a5f;">{{ steps.length }} step{{ steps.length === 1 ? "" : "s" }}</span>
    </div>
    <div class="card-body">
      <div v-if="loading" class="text-muted small"><i class="mdi mdi-loading mdi-spin"></i> Loading…</div>
      <div v-for="(s, i) in steps" :key="s.id" class="chain-step" :class="{ current: s.is_current }">
        <div class="chain-dot" :class="'dot-' + s.status">{{ s.position }}</div>
        <div class="chain-body">
          <div class="d-flex flex-wrap align-items-center" style="gap: 8px;">
            <b>{{ s.from_facility.name || "Facility " + s.from_facility.id }}</b>
            <i class="fe fe-arrow-right text-muted"></i>
            <b>{{ s.to_facility.name || (s.to_facility.id ? "Facility " + s.to_facility.id : "—") }}</b>
            <span class="badge" :class="statusClass(s.status)">{{ statusLabel(s.status) }}</span>
            <span v-if="s.is_corridor" class="badge badge-soft-info">Cross-state</span>
            <span v-if="s.is_current" class="badge badge-soft-dark">This request</span>
            <span v-if="s.code" class="badge badge-soft-success">Code {{ s.code }}</span>
            <router-link v-if="!s.is_current" :to="`/authorization-code/${s.id}`" class="small ml-auto">Open</router-link>
          </div>
          <ul class="chain-events">
            <li v-for="(e, j) in s.steps" :key="j">
              <span class="event-status">{{ statusLabel(e.status) }}</span>
              <span v-if="e.by_name"> by {{ e.by_name }}</span>
              <span class="text-muted"> · {{ e.at }}</span>
            </li>
            <li v-if="!s.steps.length" class="text-muted">Requested {{ s.requested_at }}<span v-if="s.requested_by && s.requested_by.name"> by {{ s.requested_by.name }}</span></li>
          </ul>
          <div v-if="i < steps.length - 1" class="small text-muted mt-1"><i class="fe fe-corner-down-right mr-1"></i>re-referred</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/** The referral chain of an authorization code (GET authorization_code/{id}/chain). */
export default {
  name: "ReferralChain",
  props: { codeId: { type: [Number, String], required: true } },
  data() {
    return { steps: [], loading: false };
  },
  watch: { codeId() { this.load(); } },
  mounted() { this.load(); },
  methods: {
    load() {
      this.loading = true;
      this.axios
        .get(`/api/v1/auth/authorization_code/${this.codeId}/chain`)
        .then((res) => { this.steps = res.data.data || []; })
        .catch(() => { this.steps = []; })
        .finally(() => { this.loading = false; });
    },
    statusLabel(status) {
      return { requested: "Requested", pending: "Pending", primary_approval: "Primary approval", approved: "Approved", rejected: "Declined", resubmitted: "Resubmitted" }[status] || status;
    },
    statusClass(status) {
      return { approved: "badge-soft-success", rejected: "badge-soft-danger", primary_approval: "badge-soft-primary", pending: "badge-soft-warning" }[status] || "badge-soft-secondary";
    },
  },
};
</script>

<style scoped>
@import "../reports.css";
.chain-step { display: flex; gap: 14px; position: relative; padding-bottom: 18px; }
.chain-step:not(:last-child)::before { content: ""; position: absolute; left: 15px; top: 32px; bottom: 0; width: 2px; background: #e2e8f0; }
.chain-step.current .chain-body { background: #f8fafc; border-color: #2563eb; }
.chain-dot { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; color: #fff; background: #94a3b8; flex-shrink: 0; }
.dot-approved { background: #059669; }
.dot-rejected { background: #dc2626; }
.dot-pending { background: #d97706; }
.dot-primary_approval { background: #2563eb; }
.chain-body { flex: 1; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; }
.chain-events { list-style: none; padding: 0; margin: 8px 0 0; font-size: 0.82rem; }
.chain-events li { padding: 2px 0; }
.event-status { font-weight: 600; }
</style>
